import { useEffect, useMemo, useState } from "react";
import { Button, Card, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { AESDecrypt } from "../../../encrypt-util";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowAltCircleLeft } from "@fortawesome/free-regular-svg-icons";
import { faCheckCircle, faCircleCheck, faEye, faSave } from "@fortawesome/free-solid-svg-icons";

import UploadThing from "../../../components/common/UploadThing";
import { AppDispatch, RootState } from "../template/store";
import { updateDashbaordRefresh } from "../../common/components/DataTable/slices/DataTable";
import {
  getSezCertificateRequest,
  getSezOpenDocumentUrl,
  getSezRejectedMessage,
  ISezCertificateFinalSubmitInvoice,
  ISezCertificateUploadInvoice,
  submitSezCertificateFinal,
  uploadSezCertificate,
} from "./SezActions";
import SezInvoiceDetails, {
  InvoiceDetailsData,
  SezInvoiceEditableField,
  mapSezCertificateRequestToInvoiceDetails,
} from "./SezInvoiceDetails";

interface UploadedFile {
  fileName: string;
  serverFileName: string;
  serverFilePath: string;
}

interface RelatedCertificateInvoice {
  sezInvNo: string;
  sezInvDt?: string;
  sezInvAmt?: string;
  sezInvTp?: string;
  sezOnlineReferenceNumber?: string;
}

interface CertificateUploadState {
  fileName: string;
  loading: number;
  uploadedFile: UploadedFile | null;
  uploading: boolean;
  uploaded: boolean;
  uploadedInvoices: ISezCertificateUploadInvoice[];
  relatedInvoices: RelatedCertificateInvoice[];
}

const initialCertificateUploadState: CertificateUploadState = {
  fileName: "",
  loading: 0,
  uploadedFile: null,
  uploading: false,
  uploaded: false,
  uploadedInvoices: [],
  relatedInvoices: [],
};

const FINAL_SUBMIT_LOCK_PREFIX = "sez-cert-final-submitted-";

const toDateInputValue = (value?: string | null) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const dmy = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2].padStart(2, "0")}-${dmy[1].padStart(2, "0")}`;
  return "";
};

export const downloadSezInvoiceTemplate = () => {
  window.open("/xlsx/SEZ_Invoice_Upload_Template.xlsx", "_blank", "noopener,noreferrer");
};

const SezCertUpload = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { blid, bl_no, sez_cert_req_id } = useParams();

  const profileDetails = useSelector((state: RootState) => state.userProfile.profile);
  const canEditSezFields =
    useSelector((state: RootState) => state.userProfile.rbacDetails.sezFieldEdit) === "Y";
  const linerIdFromNav = useSelector((state: RootState) => state.navbarTop.linerId);

  const APP_PATH_PREFIX = process.env.REACT_APP_APP_PATH_PREFIX || "";
  const rawReqId = sez_cert_req_id || blid || bl_no || "";
  const sezCertReqId = rawReqId ? Number(AESDecrypt(rawReqId)) : NaN;
  const isValidReqId = !!sezCertReqId && !Number.isNaN(sezCertReqId);

  const loginUser =
    profileDetails?.username || profileDetails?.partnerName || profileDetails?.email || "";
  const linerId =
    profileDetails?.partnerType === "liner" ? profileDetails?.partnerId : linerIdFromNav || "";

  const [invoiceForm, setInvoiceForm] = useState<InvoiceDetailsData | null>(null);
  const [certState, setCertState] = useState<CertificateUploadState>(initialCertificateUploadState);
  const [submitting, setSubmitting] = useState(false);
  const [finalSubmitted, setFinalSubmitted] = useState(() => {
    if (!isValidReqId) return false;
    try {
      return sessionStorage.getItem(`${FINAL_SUBMIT_LOCK_PREFIX}${sezCertReqId}`) === "1";
    } catch {
      return false;
    }
  });

  const normalizedStatus = (invoiceForm?.status || "").toUpperCase();
  const isPending = normalizedStatus === "PENDING";
  const isRejected = normalizedStatus === "REJECTED";
  const canUpload = (isPending || isRejected) && (isRejected || !finalSubmitted);

  const editableFields = useMemo<SezInvoiceEditableField[]>(() => {
    if (isPending || (isRejected && certState.uploaded)) {
      return canEditSezFields ? ["requestId", "issueDate", "remarks"] : ["remarks"];
    }
    return [];
  }, [canEditSezFields, certState.uploaded, isPending, isRejected]);

  const fetchInvoiceDetails = async () => {
    if (!isValidReqId) throw new Error("SEZ Certificate Request ID is missing");
    const result = await dispatch(getSezCertificateRequest(sezCertReqId)).unwrap();
    const mapped = mapSezCertificateRequestToInvoiceDetails(result?.data?.[0]);
    if (!mapped) throw new Error("No invoice details found");
    return mapped;
  };

  const applyInvoiceDetails = (mapped: InvoiceDetailsData, preferApi = false) => {
    setInvoiceForm((prev) => ({
      ...(mapped || {}),
      status: mapped?.status || prev?.status || "",
      ...(!preferApi && {
        requestId: prev?.requestId || mapped?.requestId || "",
        issueDate: prev?.issueDate || mapped?.issueDate || "",
        remarks: prev?.remarks || mapped?.remarks || "",
      }),
    }));
  };

  useEffect(() => {
    if (!isValidReqId) return;
    let cancelled = false;
    fetchInvoiceDetails()
      .then((mapped) => { if (!cancelled) applyInvoiceDetails(mapped); })
      .catch((e) => { if (!cancelled) toast.error(getSezRejectedMessage(e, "Failed to fetch invoice details")); });
    return () => { cancelled = true; };
  }, [dispatch, sezCertReqId]);

  const refreshInvoiceDetails = async (preferApi = false) => {
    if (!isValidReqId) return;
    try {
      const mapped = await fetchInvoiceDetails();
      applyInvoiceDetails(mapped, preferApi);
    } catch (e) {
      toast.error(getSezRejectedMessage(e, "Failed to fetch invoice details"));
    }
  };

  const clearFile = () =>
    setCertState((prev) => ({ ...initialCertificateUploadState, uploading: prev.uploading }));

  const applyCertUploadResponse = (
    result: { requestId?: string; issueDate?: string; invoices?: ISezCertificateUploadInvoice[] } | null
  ) => {
    const currentInvoiceNo = String(invoiceForm?.invoiceNo || "").trim().toUpperCase();
    const invoices = result?.invoices || [];

    const relatedInvoices = invoices
      .map((inv) => {
        const invNo = String(inv?.invoiceNumber || "").trim();
        if (!invNo || invNo.toUpperCase() === currentInvoiceNo) return null;
        const status = String(inv?.status || "").trim().toUpperCase();
        if (status !== "PENDING" && status !== "REJECTED") return null;
        return {
          sezInvNo: invNo,
          sezInvDt: String(inv?.sezInvDt || inv?.invoiceDate || "").trim() || undefined,
          sezInvAmt: String(inv?.sezInvAmt || inv?.amountInr || "").trim() || undefined,
          sezInvTp: String(inv?.sezInvTp || inv?.invoiceType || "").trim() || undefined,
          sezOnlineReferenceNumber: String(inv?.sezOnlineReferenceNumber || "").trim() || undefined,
        };
      })
      .filter(Boolean)
      .filter(
        (inv, i, arr) =>
          arr.findIndex((x) => x!.sezInvNo.toUpperCase() === inv!.sezInvNo.toUpperCase()) === i
      ) as RelatedCertificateInvoice[];

    setCertState((prev) => ({ ...prev, uploadedInvoices: invoices, relatedInvoices }));

    const matched = invoices.find(
      (inv) =>
        String(inv?.sezInvNo || inv?.invoiceNumber || "").trim().toUpperCase() === currentInvoiceNo
    );
    const uploadedRequestId = String(result?.requestId || "").trim();
    const uploadedIssueDate = toDateInputValue(matched?.issueDate ?? result?.issueDate);

    if (uploadedRequestId || uploadedIssueDate) {
      setInvoiceForm((prev) => ({
        ...(prev || {}),
        ...(uploadedRequestId && { requestId: uploadedRequestId }),
        ...(uploadedIssueDate && { issueDate: uploadedIssueDate }),
      }));
    }
  };

  const handleFulfilled = async (name: string, serverPath: string, serverName: string) => {
    if (!isValidReqId) {
      toast.error("SEZ Certificate Request ID is missing");
      return;
    }

    const file: UploadedFile = { fileName: name, serverFileName: serverName, serverFilePath: serverPath };
    setCertState((prev) => ({
      ...prev,
      fileName: name,
      loading: 100,
      uploadedFile: file,
      uploading: true,
      uploaded: false,
      uploadedInvoices: [],
      relatedInvoices: [],
    }));

    try {
      const result = await dispatch(
        uploadSezCertificate({ ...file, sezCertReqId, loginUser, locationId: 12 })
      ).unwrap();

      setCertState((prev) => ({ ...prev, uploaded: true }));
      applyCertUploadResponse(result);
      toast.success(result?.message || "SEZ Certificate uploaded successfully", {
        style: { color: "white" },
      });
      dispatch(updateDashbaordRefresh(true));
    } catch (e) {
      clearFile();
      toast.error(getSezRejectedMessage(e, "Failed to upload SEZ Certificate"));
    } finally {
      setCertState((prev) => ({ ...prev, uploading: false }));
    }
  };

  const lockFinalSubmit = () => {
    if (!isValidReqId) return;
    try {
      sessionStorage.setItem(`${FINAL_SUBMIT_LOCK_PREFIX}${sezCertReqId}`, "1");
    } catch {}
    setFinalSubmitted(true);
  };

  const handleSubmit = async () => {
    if (!canUpload) return void toast.error("Submit is only allowed when status is Pending");
    if (!isValidReqId) return void toast.error("SEZ Certificate Request ID is missing");
    if (!certState.uploaded) return void toast.error("Please upload the certificate file first");
    if (!invoiceForm?.requestId?.trim()) return void toast.error("Please enter Request ID");
    if (!invoiceForm?.issueDate?.trim()) return void toast.error("Please enter Issue Date");

    const { remarks = "", requestId, issueDate, invoiceNo, invoiceDate, sezInvNo, status } =
      invoiceForm;
    const currentInvoiceNo = String(invoiceNo || "").trim().toUpperCase();

    const invoicesPayload: ISezCertificateFinalSubmitInvoice[] =
      certState.uploadedInvoices.map((inv) => {
        const invNo = String(inv?.sezInvNo || inv?.invoiceNumber || "").trim().toUpperCase();
        return invNo === currentInvoiceNo
          ? { ...inv, remarks: remarks.trim(), issueDate: issueDate.trim() }
          : { ...inv };
      });

    const alreadyInPayload = invoicesPayload.some(
      (inv) =>
        String(inv?.sezInvNo || inv?.invoiceNumber || "").trim().toUpperCase() === currentInvoiceNo
    );
    if (currentInvoiceNo && !alreadyInPayload) {
      invoicesPayload.push({
        invoiceNumber: invoiceNo || null,
        invoiceDate: invoiceDate || null,
        issueDate: issueDate.trim(),
        sezInvNo: sezInvNo || null,
      });
    }

    setSubmitting(true);
    try {
      await dispatch(
        submitSezCertificateFinal({
          sezCertReqId,
          requestId: requestId.trim(),
          issueDate: issueDate.trim(),
          requestStatus: status || "",
          invoices: invoicesPayload,
        })
      ).unwrap();

      toast.success("Certificate submitted successfully", { style: { color: "white" } });
      lockFinalSubmit();
      clearFile();
      await refreshInvoiceDetails(true);
      dispatch(updateDashbaordRefresh(true));
    } catch (e) {
      toast.error(getSezRejectedMessage(e, "Failed to submit certificate details"));
    } finally {
      setSubmitting(false);
    }
  };

  const isDisabled =
    submitting ||
    certState.uploading ||
    !canUpload ||
    !certState.uploaded ||
    !invoiceForm?.requestId?.trim() ||
    !invoiceForm?.issueDate?.trim();

  const uploadStatusMessage = certState.uploading
    ? "Saving certificate…"
    : certState.uploaded
    ? "Certificate saved."
    : "";

  return (
    <Card className="h-100 border-0 overflow-auto">
      <style>{`
        .sez-upload-row { gap: 0.75rem; }
        .sez-upload-control { min-width: 0; }
        .sez-upload-control > div { flex-wrap: wrap; row-gap: 0.5rem; }
        .sez-upload-control span { max-width: 100%; }
        .sez-validation-card { word-break: break-word; }
        .sez-upload-dropzone {
          border: 1px dashed #c7d2e0;
          border-radius: 0.75rem;
          background: #fafbfd;
          transition: border-color 0.15s ease, background 0.15s ease;
        }
        .sez-upload-dropzone:hover { border-color: #9fb3cc; background: #f5f8fc; }
        .sez-status-pill {
          display: inline-flex;
          align-items: center;
          border-radius: 999px;
          font-weight: 600;
          padding: 0.2rem 0.75rem;
        }
        .sez-info-card {
          background: #eef6ff;
          border: 1px solid #d3e6ff;
          border-radius: 0.75rem;
        }
        .sez-section-label {
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: #8792a2;
          margin-bottom: 0.5rem;
        }
        @media (max-width: 575.98px) {
          .sez-upload-row { align-items: stretch !important; }
          .sez-upload-title, .sez-upload-control, .sez-upload-hint,
          .sez-upload-footer, .sez-view-certificate-btn, .sez-submit-wrap { width: 100%; }
          .sez-submit-wrap .btn, .sez-view-certificate-btn .btn { width: 100%; }
        }
      `}</style>

      <Card.Header className="d-flex align-items-center border-bottom sticky-top bg-white py-3 px-3 px-md-4 flex-wrap gap-2">
        <FontAwesomeIcon
          icon={faArrowAltCircleLeft}
          className="text-warning cursor-pointer fa-lg mb-1 me-2 flex-shrink-0"
          onClick={() => navigate(`${APP_PATH_PREFIX}/sezDashboard`)}
        />
        <div className="min-w-0 flex-grow-1">
          <h2 className="fs-0 text-primary mb-0 text-truncate">Certificate Upload</h2>
        </div>
      </Card.Header>

      <Card.Body className="p-3 p-sm-3 p-md-4 d-flex flex-column">
        <div className="w-100">
          {!canUpload ? (
            <div className="sez-info-card d-flex flex-wrap align-items-center gap-3 p-3 mb-4">
              <FontAwesomeIcon
                icon={faCircleCheck}
                className="text-primary"
                style={{ fontSize: "1.5rem" }}
              />
              <div className="sez-view-certificate-btn">
                <Button
                  type="button"
                  size="sm"
                  variant="falcon-primary"
                  onClick={() => {
                    if (!isValidReqId) return void toast.error("SEZ Certificate Request ID is missing");
                    if (!linerId) return void toast.error("Liner ID is missing");
                    window.open(
                      getSezOpenDocumentUrl(sezCertReqId, linerId),
                      "_blank",
                      "noopener,noreferrer"
                    );
                  }}
                >
                  <FontAwesomeIcon icon={faEye} className="me-2" />
                  View Certificate
                </Button>
              </div>
            </div>
          ) : (
            <div className="mb-4">
              <div className="d-flex flex-wrap align-items-center gap-3 sez-upload-dropzone p-3">
                <span className="fs--1 fw-semibold text-muted flex-shrink-0">Upload PDF file</span>

                <div className="flex-grow-1">
                  <UploadThing
                    name={certState.fileName}
                    loading={certState.uploading ? 50 : certState.loading}
                    fulfilled={handleFulfilled}
                    failed={() => { clearFile(); toast.error("Upload failed"); }}
                    pending={(progress: number) =>
                      setCertState((prev) => ({ ...prev, loading: progress }))
                    }
                    clear={clearFile}
                    selected={(name: string) =>
                      setCertState((prev) => ({
                        ...prev,
                        fileName: name,
                        loading: 1,
                        uploadedFile: null,
                        uploaded: false,
                        uploadedInvoices: [],
                        relatedInvoices: [],
                      }))
                    }
                    fileLimit={10}
                    fileType={["pdf"]}
                    labelWidth="clamp(9rem, 28vw, 10rem)"
                    disabled={submitting || certState.uploading}
                  />
                </div>

                {uploadStatusMessage && (
                  <span
                    className="sez-status-pill fs--2 flex-shrink-0"
                    style={{
                      background: certState.uploading ? "#eef2ff" : "#e6f7ee",
                      color: certState.uploading ? "#4c51bf" : "#1e8a5f",
                    }}
                  >
                    {certState.uploading ? (
                      <Spinner
                        animation="border"
                        size="sm"
                        className="me-2"
                        style={{ width: "0.65rem", height: "0.65rem" }}
                      />
                    ) : (
                      <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                    )}
                    {uploadStatusMessage}
                  </span>
                )}

                <small className="fs--2 text-muted ms-md-auto flex-shrink-0">
                  PDF only · Max 10 MB
                </small>
              </div>
            </div>
          )}

          <div className="mb-4">
            <SezInvoiceDetails
              invoiceForm={invoiceForm}
              compact
              editableFields={editableFields}
              onFieldChange={(field: SezInvoiceEditableField, value: string) =>
                setInvoiceForm((prev) => ({ ...(prev || {}), [field]: value }))
              }
            />
          </div>

          {certState.relatedInvoices.length > 0 && (
            <div className="mb-4">
              <div className="sez-section-label">Other invoice numbers in certificate</div>
              <Card className="border" style={{ background: "#f8fafc", borderColor: "#e3e8ef" }}>
                <Card.Body className="py-3 px-3">
                  <div className="d-flex flex-wrap gap-2">
                    {certState.relatedInvoices.map((inv) => (
                      <span
                        key={inv.sezInvNo}
                        className="badge rounded-pill fs--2 px-3 py-2"
                        style={{ background: "#e8f1ff", color: "#2c7be5", fontWeight: 600 }}
                        title={[
                          inv.sezInvDt && `Date: ${inv.sezInvDt}`,
                          inv.sezInvAmt && `Amount: ${inv.sezInvAmt}`,
                          inv.sezInvTp && `Type: ${inv.sezInvTp}`,
                          inv.sezOnlineReferenceNumber && `Ref: ${inv.sezOnlineReferenceNumber}`,
                        ]
                          .filter(Boolean)
                          .join(" | ")}
                      >
                        {inv.sezInvNo}
                      </span>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </div>
          )}

          {canUpload && (
            <div className="d-flex flex-column align-items-center mt-2 sez-submit-wrap">
              <Button
                type="button"
                size="sm"
                variant="falcon-primary"
                disabled={isDisabled}
                onClick={handleSubmit}
              >
                {submitting ? (
                  <Spinner animation="border" size="sm" className="me-2" />
                ) : (
                  <FontAwesomeIcon icon={faSave} className="me-2" />
                )}
                {submitting ? "Submitting…" : "Submit"}
              </Button>
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default SezCertUpload;
